export function productionProductLines(prod) {
  const name = String(prod?.productName || '').trim();
  const plan = String(prod?.planName || '').trim();
  const color = String(prod?.colorLabel || '').trim();
  if (plan && color) return { name: plan, color };
  if (color && name && !name.includes(color)) return { name, color };
  return { name, color: '' };
}

export const PRODUCTION_COOLDOWN_MS = 10000;

export function productionCooldownLeft(until, now = Date.now()) {
  const rem = Math.ceil((Number(until) - now) / 1000);
  return rem > 0 ? rem : 0;
}

export function parseProductionQty(value) {
  const n = Number(String(value ?? '').replace(',', '.').trim());
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function scanIds(scan = {}) {
  const ids = [scan.productId, scan.parentId, ...(Array.isArray(scan.familyIds) ? scan.familyIds : [])];
  return new Set(ids.map((id) => String(id || '').trim()).filter(Boolean));
}

export function matchOpenStagesByInputScan(stages, scan) {
  const ids = scanIds(scan);
  if (!ids.size) return [];
  const productId = String(scan?.productId || '').trim();
  return (Array.isArray(stages) ? stages : []).filter((stage) => {
    if (Number(stage.totalQuantity) === Number(stage.completedQuantity)) return false;
    const mats = Array.isArray(stage.materialIds) ? stage.materialIds : [];
    return mats.some((material) => {
      const idMs = String(material?.id_ms || '').trim();
      const parent = String(material?.product_id || '').trim();
      if (material?.exact) return Boolean(productId) && idMs === productId;
      return (idMs && ids.has(idMs)) || (parent && ids.has(parent));
    });
  });
}

export function mergeProductionTaskHeader(current, incoming = {}, taskId) {
  const id = String(incoming?.id || current?.id || taskId || '').trim();
  const same = Boolean(current && String(current.id || '') === id);
  const name = String(incoming?.name || (same ? current?.name : '') || '').trim();
  const moment = incoming?.moment || (same ? current?.moment : null) || null;
  const description = String(incoming?.description || (same ? current?.description : '') || '');
  const href = incoming?.href || (same ? current?.href : null) || null;
  const processTypeHref = incoming?.processTypeHref || (same ? current?.processTypeHref : null) || null;
  const processTypeId = incoming?.processTypeId || (same ? current?.processTypeId : null) || null;
  return {
    ...(same ? current : {}),
    ...(incoming || {}),
    id,
    name,
    moment,
    description,
    href,
    processTypeHref,
    processTypeId,
  };
}

export const PRODUCTION_PROCESS_TYPE_STORAGE_KEY = 'production_process_type_href';

export function readStoredProcessTypeHref() {
  try {
    if (typeof localStorage === 'undefined') return '';
    return String(localStorage.getItem(PRODUCTION_PROCESS_TYPE_STORAGE_KEY) || '');
  } catch {
    return '';
  }
}

export function writeStoredProcessTypeHref(href) {
  try {
    if (typeof localStorage === 'undefined') return;
    const value = String(href || '').trim();
    if (value) localStorage.setItem(PRODUCTION_PROCESS_TYPE_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

// Совпадает с backend productionProcessTypeSettings: весы только у дробления и набивки
export const DROBLENIE_PROCESS_TYPE_ID = '71d651cf-519e-11f0-0a80-15ea000809af';
export const NABIVKA_PROCESS_TYPE_ID = '41f896cb-519f-11f0-0a80-0bb10008edf3';
export const DEFAULT_WEIGHT_PROCESS_TYPE_IDS = new Set([
  DROBLENIE_PROCESS_TYPE_ID,
  NABIVKA_PROCESS_TYPE_ID,
]);

export function processTypeRequiresWeight(type) {
  if (!type) return false;
  if (Object.prototype.hasOwnProperty.call(type, 'requires_weight')) {
    return Boolean(type.requires_weight);
  }
  return DEFAULT_WEIGHT_PROCESS_TYPE_IDS.has(String(type.id || ''));
}

export const CRUMB_PRODUCT_ID = 'c2b36f6a-f3e6-11ed-0a80-0d770010d904';
export const CRUMB_PRODUCT_IDS = new Set([
  CRUMB_PRODUCT_ID,
  '03669c4f-192b-11f0-0a80-17560033219f',
]);

export function isCrumbProduct(productId) {
  return CRUMB_PRODUCT_IDS.has(String(productId || '').trim());
}

export function parseManualKg(value) {
  const n = parseFloat(String(value || '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Number(n.toFixed(3)) : 0;
}

export function kgFromScale(data) {
  const weight = Number(data?.weight || 0);
  if (!Number.isFinite(weight)) return 0;
  if (data?.unit === 'g') return Number((weight / 1000).toFixed(3));
  return Number(weight.toFixed(3));
}

/** Новый цех: крошка — ручной ввод как на /scale; остальные — живой вес с весов. */
export function resolveProductionNextWeightKg({
  productId,
  scaleKg,
  snapshotKg,
  manualWeight,
} = {}) {
  if (isCrumbProduct(productId)) {
    return parseManualKg(manualWeight) || Number(Number(snapshotKg || 0).toFixed(3)) || 0;
  }
  const live = Number(scaleKg || 0);
  return Number.isFinite(live) && live > 0 ? Number(live.toFixed(3)) : 0;
}

export function pickWeightProcessType(types, { selectedHref, taskHref, taskId } = {}) {
  const list = Array.isArray(types) ? types : [];
  const selected = String(selectedHref || '').trim();
  const task = String(taskHref || '').trim();
  const id = String(taskId || '').trim();
  return list.find((type) => task && String(type?.href || '') === task)
    || list.find((type) => id && String(type?.id || '') === id)
    || list.find((type) => selected && String(type?.href || '') === selected)
    || null;
}

export function pickProcessTypeHref(types, { storedHref, taskHref } = {}) {
  const list = Array.isArray(types) ? types : [];
  const known = new Set(list.map((type) => String(type?.href || '')).filter(Boolean));
  const task = String(taskHref || '').trim();
  if (task && known.has(task)) return task;
  const stored = String(storedHref || '').trim();
  if (stored && known.has(stored)) return stored;
  return '';
}

export function shouldKeepNamedTask(current, taskId) {
  return Boolean(
    current
    && String(current.id || '') === String(taskId || '')
    && String(current.name || '').trim(),
  );
}

export function checkMaterialsRequestParams({ planId, quantity, product } = {}) {
  const params = {
    planId,
    quantity: String(quantity || 1),
    localNames: '1',
  };
  if (product?.productId) params.productId = product.productId;
  if (product?.colorLabel) params.colorLabel = product.colorLabel;
  if (product?.productName) params.productName = product.productName;
  if (Array.isArray(product?.characteristics) && product.characteristics.length) {
    params.characteristics = JSON.stringify(product.characteristics);
  }
  return params;
}



